/* ==========================================================================
   ZELICAT THEME — global.js
   Loaded on every page (deferred). Vanilla JS, no dependencies.
   Contains: the shared ZC helper object, toast, mobile menu, sticky header,
   scroll reveal. Section-specific scripts live in assets/*.js and call ZC.mount().
   ========================================================================== */
(() => {
  'use strict';

  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Settings + translated strings printed by layout/theme.liquid */
  let config = { routes: {}, strings: {}, cartType: 'drawer' };
  try { config = JSON.parse(document.getElementById('zc-config').textContent); } catch (e) { /* keep defaults */ }

  /* ------------------------------------------------------------------ helpers shared by every script */
  const ZC = window.ZC = window.ZC || {};
  ZC.$ = $;
  ZC.$$ = $$;
  ZC.config = config;
  ZC.reduceMotion = reduceMotion;

  /* ZC.mount(fn): run fn(root) now, and again for any section the merchant adds or edits
     in the theme editor (Shopify fires "shopify:section:load"). Keeps every section live in the editor. */
  ZC.mount = (fn) => {
    const run = (root) => { try { fn(root); } catch (err) { console.error('[ZeliCat]', err); } };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => run(document));
    else run(document);
    document.addEventListener('shopify:section:load', (e) => run(e.target));
  };

  /* ZC.once(el, key): true the first time it is called for this element, false afterwards. */
  ZC.once = (el, key = 'init') => {
    const attr = 'data-zc-' + key;
    if (el.hasAttribute(attr)) return false;
    el.setAttribute(attr, '');
    return true;
  };

  /* ZC.focusTrap(container): keeps Tab inside an open dialog. Returns a function that removes the trap. */
  ZC.focusTrap = (container) => {
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      const f = $$('a[href], button:not([disabled]):not([tabindex="-1"]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])', container)
        .filter((el) => el.offsetParent !== null);
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  };

  /* ------------------------------------------------------------------ toast */
  let toastTimer;
  ZC.toast = (msg) => {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = msg;
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 2600);
  };

  /* ------------------------------------------------------------------ mobile menu */
  function initMenu(root) {
    const nav = $('#mobile-nav', root);
    const openBtn = $('[data-menu-open]', root);
    if (!nav || !openBtn || !ZC.once(nav, 'menu')) return;
    const closeBtn = $('[data-menu-close]', nav);
    let release = null;
    const close = () => {
      nav.hidden = true;
      openBtn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      if (release) release();
      openBtn.focus();
    };
    const open = () => {
      nav.hidden = false;
      openBtn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      release = ZC.focusTrap($('.mobile-nav__panel', nav));
      closeBtn.focus();
    };
    openBtn.addEventListener('click', open);
    $$('[data-menu-close]', nav).forEach((b) => b.addEventListener('click', close));
    nav.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
    window.matchMedia('(min-width: 1101px)').addEventListener('change', (m) => { if (m.matches && !nav.hidden) close(); });
  }

  /* ------------------------------------------------------------------ header: hide on scroll down, show on scroll up */
  function initHeader(root) {
    const header = $('[data-header]', root);
    if (!header || header.dataset.zcBound) return;
    header.dataset.zcBound = '1';
    const mode = header.getAttribute('data-sticky') || 'scroll-up';   // scroll-up | always | none
    let lastY = window.scrollY;
    let ticking = false;
    const update = () => {
      const y = window.scrollY;
      const delta = y - lastY;
      header.classList.toggle('is-compact', y > 80);
      if (mode === 'scroll-up') {
        if (y < 120) header.classList.remove('is-hidden');
        else if (delta > 6) header.classList.add('is-hidden');
        else if (delta < -6) header.classList.remove('is-hidden');
      }
      lastY = y;
      ticking = false;
    };
    if (mode !== 'none') window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    header.addEventListener('focusin', () => header.classList.remove('is-hidden'));
  }

  /* ------------------------------------------------------------------ scroll reveal */
  let revealObserver = null;
  function initReveal(root) {
    const items = $$('.reveal:not(.is-in), .reveal-img:not(.is-in)', root);
    if (!items.length) return;
    if (!('IntersectionObserver' in window)) { items.forEach((i) => i.classList.add('is-in')); return; }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); revealObserver.unobserve(e.target); } });
      }, { rootMargin: '0px 0px -6% 0px', threshold: 0.04 });
    }
    items.forEach((i) => revealObserver.observe(i));
  }
  /* Inside the theme editor, show everything straight away so edits are visible. */
  if (window.Shopify && window.Shopify.designMode) document.documentElement.classList.add('is-design-mode');

  /* ------------------------------------------------------------------ copy link buttons ([data-copy-link]) */
  function initCopyLink(root) {
    $$('[data-copy-link]', root).forEach((btn) => {
      if (!ZC.once(btn, 'copy')) return;
      const s = config.strings;
      const label = btn.textContent;
      btn.addEventListener('click', async () => {
        const url = location.href.split('#')[0];
        let ok = false;
        try { await navigator.clipboard.writeText(url); ok = true; } catch (e) {
          try {
            const t = document.createElement('textarea');
            t.value = url; t.style.position = 'fixed'; t.style.opacity = '0';
            document.body.appendChild(t); t.select(); ok = document.execCommand('copy'); t.remove();
          } catch (e2) { ok = false; }
        }
        ZC.toast(ok ? s.linkCopied : s.copyFallback);
        if (ok) {
          btn.classList.add('is-done');
          btn.textContent = btn.dataset.copiedLabel || label;
          setTimeout(() => { btn.classList.remove('is-done'); btn.textContent = label; }, 2200);
        }
      });
    });
  }

  /* country / language selectors submit their form as soon as one is chosen */
  document.addEventListener('change', (e) => {
    const sel = e.target.closest('[data-localization-select]');
    if (sel && sel.form) sel.form.submit();
  });

  ZC.mount((root) => {
    initMenu(document);
    initHeader(document);
    initReveal(root);
    initCopyLink(root);
  });
})();
